/**
 * Shared bilingual copy used by the 3 landing variants.
 * Each variant renders its own visual treatment of the same content.
 */
import {
  Lightbulb,
  Sparkles,
  Coins,
  FileText,
  Grid3x3,
  Search,
  Cog,
  Users,
  Calendar,
  Megaphone,
  Wallet,
  ShieldAlert,
  Presentation,
} from "lucide-react";

export const LANDING_COPY = {
  es: {
    metaTitle: "Atelier Business — Lanza tu idea. Consigue capital.",
    metaDesc:
      "Construye un business plan completo y un pitch deck listo para inversores en una tarde. IA + 11 módulos + proyecciones a 5 años. Desde 0 €.",
    nav: {
      templates: "Plantillas",
      pricing: "Precios",
      login: "Iniciar sesión",
      cta: "Empezar gratis",
    },
    hero: {
      pill: "Pruébalo aquí mismo — sin registro",
      title_a: "Tu business plan a 5 años,",
      title_b: "escrito por IA en una tarde.",
      subtitle:
        "Olvida los 8.000 € de un consultor y las plantillas de Excel obsoletas. Cuenta tu idea y la IA escribe los 11 módulos, las finanzas a 5 años y el pitch deck listo para inversores.",
      cta_primary: "Crear cuenta gratis",
      cta_secondary: "Ver demo en vivo",
      proofline: "Sin tarjeta · 1 plan gratis · cancela cuando quieras",
      micro_a: "10 min",
      micro_a_label: "primera versión",
      micro_b: "12 slides",
      micro_b_label: "pitch deck",
      micro_c: "5 años",
      micro_c_label: "proyección financiera",
    },
    savings: {
      eyebrow: "Coste real vs Atelier",
      title: "Ahorra 8.000 € y tres semanas. Sin contratar a nadie.",
      subtitle:
        "El mercado cobra una fortuna por algo que la IA escribe contigo en una tarde. Tú decides dónde invertir ese dinero: en producto, en talento o en tus primeros clientes.",
      rows: [
        { k: "consultor", label: "Consultor de estrategia", price: "8.000 €", time: "3 semanas", note: "PDF estático, sin edición posterior" },
        { k: "freelance", label: "Freelance + plantillas Excel", price: "1.200 €", time: "2 semanas", note: "Hojas que romperás al primer cambio" },
        { k: "agencia", label: "Agencia de innovación", price: "12.000 €+", time: "6–8 semanas", note: "Mínimo 3 reuniones por iteración" },
        { k: "atelier", label: "Atelier Business", price: "49,90 €", time: "1 hora", note: "Plan completo · IA + 11 módulos editables + pitch deck export", highlight: true },
      ],
      footer: "Tu idea no necesita un MBA. Necesita un workspace que escriba contigo.",
    },
    marquee: {
      eyebrow: "12 plantillas, 12 sectores. Encuentra el tuyo.",
      industries: [
        "B2B SaaS",
        "Café & Coworking",
        "Academia online",
        "Agencia digital",
        "Food truck",
        "Inmobiliaria",
        "Restaurante",
        "E-commerce",
        "Consultoría",
        "Clínica estética",
        "Marketplace",
        "Hospitality",
      ],
    },
    steps: {
      eyebrow: "Cómo funciona",
      title: "De una idea a una ronda en 3 pasos.",
      items: [
        {
          k: "01",
          icon: Lightbulb,
          title: "Cuéntanos tu idea",
          desc: "5 preguntas rápidas. Nombre, sector, propuesta de valor. Sin formularios infinitos.",
          time: "2 min",
        },
        {
          k: "02",
          icon: Sparkles,
          title: "La IA escribe tu plan",
          desc: "11 módulos rellenados con coherencia: mercado, finanzas, equipo, riesgos. Edítalo a tu gusto.",
          time: "10 min",
        },
        {
          k: "03",
          icon: Coins,
          title: "Captura inversión",
          desc: "Pitch deck de 12 slides, finanzas a 5 años y modo inversor. Exporta a PPTX o PDF.",
          time: "1 click",
        },
      ],
    },
    bento: {
      eyebrow: "11 módulos. Una sola plataforma.",
      title: "Todo lo que necesita un fundador, sin abrir Excel.",
      items: [
        { k: "concept", icon: Lightbulb, title: "Concepto", desc: "Idea, misión, visión, socios y equity." },
        { k: "canvas", icon: Grid3x3, title: "Business Model Canvas", desc: "Los 9 bloques editables y exportables." },
        { k: "market", icon: Search, title: "Mercado & TAM/SAM/SOM", desc: "Buyer personas, competencia, SWOT." },
        { k: "ops", icon: Cog, title: "Operaciones", desc: "Servicios, márgenes, proveedores clave." },
        { k: "hr", icon: Users, title: "Equipo & RRHH", desc: "Perfiles, salarios y plan de contratación." },
        { k: "calendar", icon: Calendar, title: "Roadmap", desc: "Calendario anual y plan a 5 años." },
        { k: "marketing", icon: Megaphone, title: "Marketing & CAC", desc: "Mix B2B / B2C, CPL y CAC objetivo." },
        { k: "budget", icon: Wallet, title: "Finanzas 5 años", desc: "Cash flow, escenarios, stress test." },
        { k: "risks", icon: ShieldAlert, title: "Riesgos", desc: "Matriz de mitigación lista para due diligence." },
        { k: "exec", icon: FileText, title: "Executive Summary", desc: "Resumen ejecutivo generado por IA." },
        { k: "deck", icon: Presentation, title: "Pitch Deck", desc: "12 slides + Cap Table + modo inversor." },
      ],
    },
    privacy: {
      eyebrow: "Tu idea es solo tuya",
      title: "Encriptación AES-256. Cero entrenamiento con tus datos.",
      subtitle:
        "Cada plan vive en su propio espacio aislado. Solo tú y los colaboradores que invitas tienen acceso.",
      points: [
        {
          k: "encryption",
          title: "AES-256 en tránsito y en reposo",
          desc: "TLS 1.3 y cifrado a nivel de base de datos. La misma protección que usa la banca.",
        },
        {
          k: "no_training",
          title: "Nunca entrenamos IA con tu plan",
          desc: "Tus datos no se usan para mejorar modelos. Ni los nuestros, ni los de terceros.",
        },
        {
          k: "isolation",
          title: "Workspaces aislados",
          desc: "Cada proyecto tiene su propio contenedor. Nadie ve tu idea salvo quien tú decidas.",
        },
        {
          k: "export",
          title: "Exporta y vete cuando quieras",
          desc: "PDF, PPTX y JSON. Tu plan es tuyo. Sin lock-in, sin letra pequeña.",
        },
      ],
    },
    proof: {
      eyebrow: "Pensado para conseguir SÍes",
      title: "Diseñado con +50 founders, VCs y aceleradoras.",
      stats: [
        { value: "1 tarde", label: "Business plan completo" },
        { value: "12 slides", label: "Pitch deck para inversor" },
        { value: "5 años", label: "Proyecciones + stress test" },
        { value: "12 sectores", label: "Plantillas reales" },
      ],
      testimonials: [
        {
          quote: "Tenía la idea en la cabeza desde hace años. En una tarde tenía un plan defendible, números y deck.",
          name: "Lucía R.",
          role: "Fundadora · Plataforma EdTech",
        },
        {
          quote: "El pitch deck generado fue el que usamos en la primera reunión con el VC. Cerramos €350k.",
          name: "Mateo G.",
          role: "Co-founder · Marketplace B2B",
        },
        {
          quote: "Estamos ahorrándole a cada cliente 3 semanas de consultoría. Es nuestro nuevo arma comercial.",
          name: "Núria T.",
          role: "Partner · Estudio de estrategia",
        },
      ],
    },
    pricing: {
      eyebrow: "Tres planes. Empieza gratis.",
      title: "Sin tarjeta, sin riesgo. Cancela cuando quieras.",
      see_all: "Ver todos los detalles",
      most_popular: "Más popular",
      tiers: [
        {
          k: "free",
          name: "Free",
          price: "0 €",
          per: "para siempre",
          features: ["2 plantillas completas", "1 business plan propio", "Demo + exportación PDF"],
          cta: "Empezar gratis",
          to: "/register",
        },
        {
          k: "starter",
          name: "Starter",
          price: "49,90 €",
          per: "/ mes",
          features: ["6 plantillas a elegir", "1 business plan + IA", "Pitch deck PPTX / PDF"],
          cta: "Activar Starter",
          to: "/pricing",
        },
        {
          k: "pro",
          name: "Pro",
          price: "99,90 €",
          per: "/ mes",
          features: ["12 plantillas ilimitadas", "10 planes / mes con IA", "Stress test + Cap Table"],
          cta: "Activar Pro",
          to: "/pricing",
        },
      ],
    },
    final: {
      eyebrow: "Tu turno",
      title_a: "El mundo necesita tu idea.",
      title_b: "Dale forma hoy.",
      cta: "Crear mi cuenta gratis",
      sub: "Sin tarjeta · 60 segundos para empezar",
    },
    footer_rights: "Atelier Business · Diseñado para fundadores ambiciosos.",
    typewriter_seed: [
      "Plataforma EdTech para profesionales LATAM…",
      "Café & coworking en barrio creativo…",
      "Marketplace B2B de logística sostenible…",
      "App de bienestar mental con IA…",
      "Food truck de ramen artesanal…",
    ],
  },
  en: {
    metaTitle: "Atelier Business — Launch your idea. Get funded.",
    metaDesc:
      "Write a complete business plan + investor-ready pitch deck in one afternoon. AI + 11 modules + 5-year projections. From $0.",
    nav: { templates: "Templates", pricing: "Pricing", login: "Sign in", cta: "Start free" },
    hero: {
      pill: "Try it right here — no signup",
      title_a: "Your 5-year business plan,",
      title_b: "written by AI in one afternoon.",
      subtitle:
        "Skip the $8,000 consultant and the outdated Excel templates. Tell AI your idea — get 11 coherent modules, 5-year financials and an investor-ready deck.",
      cta_primary: "Start free",
      cta_secondary: "See the live demo",
      proofline: "No card · 1 free plan · cancel anytime",
      micro_a: "10 min",
      micro_a_label: "first draft",
      micro_b: "12 slides",
      micro_b_label: "pitch deck",
      micro_c: "5 years",
      micro_c_label: "financial projection",
    },
    savings: {
      eyebrow: "Real cost vs Atelier",
      title: "Save $8,000 and three weeks. Without hiring anyone.",
      subtitle:
        "The market charges a fortune for something AI can write with you in one afternoon. Spend that money on product, talent or your first customers — not on slides.",
      rows: [
        { k: "consultor", label: "Strategy consultant", price: "$8,000", time: "3 weeks", note: "Static PDF, no edits later" },
        { k: "freelance", label: "Freelancer + Excel templates", price: "$1,200", time: "2 weeks", note: "Sheets that break on first change" },
        { k: "agencia", label: "Innovation agency", price: "$12,000+", time: "6–8 weeks", note: "At least 3 meetings per iteration" },
        { k: "atelier", label: "Atelier Business", price: "$49.90", time: "1 hour", note: "Full plan · AI + 11 editable modules + pitch deck export", highlight: true },
      ],
      footer: "Your idea doesn't need an MBA. It needs a workspace that writes with you.",
    },
    marquee: {
      eyebrow: "12 templates, 12 industries. Find yours.",
      industries: [
        "B2B SaaS",
        "Coffee & Coworking",
        "Online Academy",
        "Digital Agency",
        "Food Truck",
        "Real Estate",
        "Restaurant",
        "E-commerce",
        "Consulting",
        "Aesthetic Clinic",
        "Marketplace",
        "Hospitality",
      ],
    },
    steps: {
      eyebrow: "How it works",
      title: "From an idea to a round in 3 steps.",
      items: [
        {
          k: "01",
          icon: Lightbulb,
          title: "Tell us your idea",
          desc: "5 quick questions. Name, industry, value prop. No endless forms.",
          time: "2 min",
        },
        {
          k: "02",
          icon: Sparkles,
          title: "AI writes your plan",
          desc: "11 coherent modules: market, financials, team, risks. Edit anything.",
          time: "10 min",
        },
        {
          k: "03",
          icon: Coins,
          title: "Raise capital",
          desc: "12-slide pitch deck, 5-year financials and investor mode. Export to PPTX or PDF.",
          time: "1 click",
        },
      ],
    },
    bento: {
      eyebrow: "11 modules. One platform.",
      title: "Everything a founder needs, without touching Excel.",
      items: [
        { k: "concept", icon: Lightbulb, title: "Concept", desc: "Idea, mission, vision, partners and equity." },
        { k: "canvas", icon: Grid3x3, title: "Business Model Canvas", desc: "The 9 blocks, editable and exportable." },
        { k: "market", icon: Search, title: "Market & TAM/SAM/SOM", desc: "Buyer personas, competitors, SWOT." },
        { k: "ops", icon: Cog, title: "Operations", desc: "Services, margins, key suppliers." },
        { k: "hr", icon: Users, title: "Team & HR", desc: "Roles, salaries and hiring plan." },
        { k: "calendar", icon: Calendar, title: "Roadmap", desc: "12-month calendar and 5-year plan." },
        { k: "marketing", icon: Megaphone, title: "Marketing & CAC", desc: "B2B / B2C mix, target CPL and CAC." },
        { k: "budget", icon: Wallet, title: "5-year financials", desc: "Cash flow, scenarios, stress test." },
        { k: "risks", icon: ShieldAlert, title: "Risks", desc: "Mitigation matrix, ready for due diligence." },
        { k: "exec", icon: FileText, title: "Executive Summary", desc: "AI-generated executive summary." },
        { k: "deck", icon: Presentation, title: "Pitch Deck", desc: "12 slides + Cap Table + investor mode." },
      ],
    },
    privacy: {
      eyebrow: "Your idea stays yours",
      title: "AES-256 encryption. Zero training on your data.",
      subtitle: "Every plan lives in its own isolated workspace. Only you and the collaborators you invite can see it.",
      points: [
        {
          k: "encryption",
          title: "AES-256 in transit and at rest",
          desc: "TLS 1.3 and database-level encryption. The same protection used in banking.",
        },
        {
          k: "no_training",
          title: "We never train AI on your plan",
          desc: "Your data is not used to improve models. Not ours, not third parties'.",
        },
        {
          k: "isolation",
          title: "Isolated workspaces",
          desc: "Each project has its own container. Nobody sees your idea unless you decide.",
        },
        {
          k: "export",
          title: "Export and leave whenever",
          desc: "PDF, PPTX and JSON. Your plan is yours. No lock-in, no fine print.",
        },
      ],
    },
    proof: {
      eyebrow: "Built to land YES",
      title: "Crafted with 50+ founders, VCs and accelerators.",
      stats: [
        { value: "1 afternoon", label: "Complete business plan" },
        { value: "12 slides", label: "Investor pitch deck" },
        { value: "5 years", label: "Projections + stress test" },
        { value: "12 industries", label: "Real templates" },
      ],
      testimonials: [
        {
          quote: "I'd had the idea for years. In one afternoon I had a defensible plan, numbers and a deck.",
          name: "Lucia R.",
          role: "Founder · EdTech platform",
        },
        {
          quote: "The generated deck is what we used in our first VC meeting. We closed €350k.",
          name: "Mateo G.",
          role: "Co-founder · B2B Marketplace",
        },
        {
          quote: "We save each client 3 weeks of consulting. It's our new commercial weapon.",
          name: "Nuria T.",
          role: "Partner · Strategy studio",
        },
      ],
    },
    pricing: {
      eyebrow: "Three plans. Start free.",
      title: "No card, no risk. Cancel anytime.",
      see_all: "See all the details",
      most_popular: "Most popular",
      tiers: [
        {
          k: "free",
          name: "Free",
          price: "$0",
          per: "forever",
          features: ["2 full templates", "1 own business plan", "Demo + PDF export"],
          cta: "Start free",
          to: "/register",
        },
        {
          k: "starter",
          name: "Starter",
          price: "$49.90",
          per: "/ mo",
          features: ["Pick 6 templates", "1 plan + full AI", "PPTX / PDF deck"],
          cta: "Activate Starter",
          to: "/pricing",
        },
        {
          k: "pro",
          name: "Pro",
          price: "$99.90",
          per: "/ mo",
          features: ["Unlimited 12 templates", "10 plans / mo with AI", "Stress test + Cap Table"],
          cta: "Activate Pro",
          to: "/pricing",
        },
      ],
    },
    final: {
      eyebrow: "Your move",
      title_a: "The world needs your idea.",
      title_b: "Shape it today.",
      cta: "Create my free account",
      sub: "No card · 60 seconds to start",
    },
    footer_rights: "Atelier Business · Made for ambitious founders.",
    typewriter_seed: [
      "EdTech platform for LATAM professionals…",
      "Café & coworking in a creative neighborhood…",
      "B2B marketplace for sustainable logistics…",
      "Mental wellness app powered by AI…",
      "Artisanal ramen food truck…",
    ],
  },
};
